import { Directive, ElementRef, HostListener, Input } from '@angular/core';

/**
 * Directive to restrict input to numeric values only and optionally limit the maximum length.
 * Usage:
 * <input numericOnly [maxLength]="5"> - Will restrict input to numbers only with max 5 digits
 * <input numericOnly> - Will restrict input to numbers only with no length limit
 */
@Directive({
  selector: '[numericOnly]',
  standalone: true
})
export class NumericOnlyDirective {
  @Input() maxLength: number = 5; // Default to 5 digits

  constructor(private el: ElementRef) {}

  @HostListener('input', ['$event'])
  onInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const initialValue = input.value;
    
    // Replace any non-numeric character with empty string
    const sanitizedValue = initialValue.replace(/[^0-9]/g, '');
    
    // Enforce maxLength if specified
    const truncatedValue = sanitizedValue.substring(0, this.maxLength);
    
    // Set the sanitized and truncated value back
    if (initialValue !== truncatedValue) {
      input.value = truncatedValue;
      // Dispatch input event to update NgModel
      input.dispatchEvent(new Event('input'));
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    if (event.clipboardData) {
      const pastedInput = event.clipboardData.getData('text/plain');
      // Filter out non-numeric characters
      const sanitizedValue = pastedInput.replace(/[^0-9]/g, '');
      // Enforce maxLength
      const truncatedValue = sanitizedValue.substring(0, this.maxLength);
      
      // Insert at cursor position
      document.execCommand('insertText', false, truncatedValue);
    }
  }

  @HostListener('keypress', ['$event'])
  onKeyPress(event: KeyboardEvent) {
    // Allow only numeric input (0-9)
    const inputChar = String.fromCharCode(event.charCode);
    const pattern = /[0-9]/;
    
    if (!pattern.test(inputChar)) {
      // If input is not a number, prevent it
      event.preventDefault();
    }
    
    // Also prevent input if it would exceed maxLength
    const input = event.target as HTMLInputElement;
    if (input.value.length >= this.maxLength && input.selectionStart === input.selectionEnd) {
      event.preventDefault();
    }
  }
}